import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import isEmpty from 'lodash/isEmpty';
import { validateSessionFeedback, ValidationResponse } from '../services/validationService';
import { useTranslate } from '../lib/i18n';
import { useCreateSessionFeedback } from '../hooks';
import Button from './common/Button';
import Checkbox from './common/Checkbox';
import { Smile } from './common/Smile';
import { Frown } from './common/Frown';
import { Meh } from './common/Meh';

const FeedbackStep = {
  RATING: 'rating',
  TEXT: 'text',
  CONSENT: 'consent',
};

const MAX_CHARS = 500;

interface Props {
  setShowFeedbackOverlay: (show: boolean) => void;
  onFeedbackSubmitted: () => void;
}

const FeedbackOverlay = ({ setShowFeedbackOverlay, onFeedbackSubmitted }: Props) => {
  const t = useTranslate();
  const [step, setStep] = useState(FeedbackStep.RATING);
  const [rating, setRating] = useState<number | null>(null);
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [consentQuote, setConsentQuote] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isPrimaryConsentSubmitLoading, setIsPrimaryConsentSubmitLoading] =
    useState<boolean>(false);

  const { mutateAsync: submitFeedback, isPending: isSubmittingFeedback } =
    useCreateSessionFeedback();

  const clearValidationError = () => setValidationError(null);
  const isValid = isEmpty(validationError);

  useEffect(() => {
    // Disable scrolling when the overlay is mounted
    document.body.style.overflow = 'hidden';
    return () => {
      // Re-enable scrolling when the overlay is unmounted
      document.body.style.overflow = '';
    };
  }, []);

  const handleValidationErrors = (validation: ValidationResponse) => {
    if (validation.success) {
      clearValidationError();
    } else {
      if (validation.error) {
        // Handle special format for invalid character messages
        if (validation.error.includes('||')) {
          const [key, char] = validation.error.split('||');
          setValidationError(t(key || '', { char }) ?? null);
        } else {
          setValidationError(t(validation.error) ?? null);
        }
      }
    }
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target.value;
    if (input.length <= MAX_CHARS) {
      setFeedbackText(input);

      // Validate input
      const validation = validateSessionFeedback({
        rating,
        feedbackText: input,
      });

      handleValidationErrors(validation);
    }
  };

  const handleSubmitFeedback = async () => {
    try {
      const validation = validateSessionFeedback({
        rating,
        feedbackText,
      });

      handleValidationErrors(validation);

      if (!validation.success) {
        throw new Error('Validation failed');
      }

      await submitFeedback({
        rating,
        feedbackText,
        consentQuote,
      });

      // Notify widget.js to close, which will close the overlay
      onFeedbackSubmitted();
      // Also notify about feedback modal closing
      window.parent.postMessage({ type: 'widget:feedbackModalClose' }, '*');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const getIconState = (rating: number | null, currentId: number) => {
    if (rating === currentId) {
      return 'selected';
    } else if (rating === null) {
      return 'default';
    } else {
      return 'inactive';
    }
  };

  const getIconSize = (rating: number | null, currentId: number) => {
    const state = getIconState(rating, currentId);
    return state === 'selected' ? 'h-12 w-12' : 'h-8 w-8';
  };

  const renderContent = () => {
    switch (step) {
      case FeedbackStep.RATING:
        return (
          <div className="mx-4 max-w-sm space-y-6 rounded-lg bg-popover p-6 text-popover-foreground">
            <p className="text-center">{t('feedback.title')}</p>
            <p className="text-center text-sm text-muted-foreground">
              {t('feedback.collectingFeedback')}
            </p>
            <div className="mb-6 grid grid-cols-3 gap-4">
              <button
                onClick={async () => {
                  setRating(1);
                  await submitFeedback({
                    rating: 1,
                    feedbackText: '',
                    consentQuote: false,
                  });
                }}
                className={`mx-auto flex h-14 w-14 flex-col items-center justify-center rounded-full p-1 text-success`}
              >
                <Smile state={getIconState(rating, 1)} className={getIconSize(rating, 1)} />
              </button>
              <button
                onClick={async () => {
                  setRating(0);
                  await submitFeedback({
                    rating: 0,
                    feedbackText: '',
                    consentQuote: false,
                  });
                }}
                className={`mx-auto flex h-14 w-14 flex-col items-center justify-center rounded-full p-1 text-warning`}
              >
                <Meh state={getIconState(rating, 0)} className={getIconSize(rating, 0)} />
              </button>
              <button
                onClick={async () => {
                  setRating(-1);
                  await submitFeedback({
                    rating: -1,
                    feedbackText: '',
                    consentQuote: false,
                  });
                }}
                className={`mx-auto flex h-14 w-14 flex-col items-center justify-center rounded-full p-1 text-destructive`}
              >
                <Frown state={getIconState(rating, -1)} className={getIconSize(rating, -1)} />
              </button>
            </div>
            {rating !== null && (
              <>
                <div className="space-y-1">
                  <textarea
                    value={feedbackText}
                    onChange={handleFeedbackChange}
                    placeholder={t('feedback.writeMessage')}
                    className={`h-32 w-full resize-none rounded border bg-input/25 p-4 text-sm text-foreground focus:outline-none ${
                      isValid ? 'border-secondary' : 'border-destructive'
                    } ${isValid ? 'focus:border-secondary' : 'focus:border-destructive'}`}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-destructive">{validationError}</span>
                    <span
                      className={`text-xs ${
                        feedbackText.length >= MAX_CHARS
                          ? 'text-destructive'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {feedbackText.length}/{MAX_CHARS}
                    </span>
                  </div>
                </div>
                <Button
                  variant="primary"
                  onClick={async () => {
                    await submitFeedback({
                      rating,
                      feedbackText,
                      consentQuote: false,
                    });
                    setStep(FeedbackStep.CONSENT);
                  }}
                  disabled={!isValid || isSubmittingFeedback || isPrimaryConsentSubmitLoading}
                >
                  {t('feedback.sendFeedback')}
                </Button>
              </>
            )}
          </div>
        );

      case FeedbackStep.CONSENT:
        return (
          <div className="mx-4 max-w-sm space-y-4 rounded-lg bg-popover p-6 text-popover-foreground">
            <p className="text-center">{t('feedback.title')}</p>
            <p className="text-sm">{t('feedback.thankYou')}</p>
            <p className="text-sm">{t('feedback.anonymousFeedbackUsage')}</p>
            <p className="text-sm">{t('feedback.conversationPrivacy')}</p>

            <hr />

            <div>
              <Checkbox
                name="consentQuote"
                checked={consentQuote}
                onChange={(event) => setConsentQuote(event.target.checked)}
                label={t('feedback.consentToUse')}
              />
            </div>
            <Button
              variant="primary"
              onClick={async () => {
                setIsPrimaryConsentSubmitLoading(true);
                try {
                  await handleSubmitFeedback();
                } finally {
                  setIsPrimaryConsentSubmitLoading(false);
                }
              }}
              disabled={isPrimaryConsentSubmitLoading || !!validationError}
            >
              <div className="flex min-h-[24px] items-center justify-center">
                {isPrimaryConsentSubmitLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t('feedback.saveAndClose')
                )}
              </div>
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  const handleOverlayClose = () => {
    setShowFeedbackOverlay(false);
    // Notify widget about feedback modal closing
    window.parent.postMessage({ type: 'widget:feedbackModalClose' }, '*');
  };

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="fixed inset-0 z-99999 flex items-center justify-center bg-black/75"
        onClick={handleOverlayClose}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={handleOverlayClose}
            className="absolute top-3 right-4 z-10 text-muted-foreground hover:text-foreground transition-colors font-bold px-2 text-sm"
            aria-label="Close feedback"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleOverlayClose();
              }
            }}
          >
            ✕
          </button>
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default FeedbackOverlay;
